import { Outlet } from 'react-router-dom';
import { CreditCard, Settings as SettingsIcon, SlidersHorizontal, Users } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, type TabItem } from '@/components/ui/Tabs';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { ROUTES } from '@/routes/paths';

/**
 * Paso 3 del plan (`docs/FRONTEND_PLAN.md` §4): Ajustes generales.
 *
 * La pantalla de Ajustes tiene cuatro partes y se reparte en dos entradas de la navegación:
 *  - `Integraciones` tiene su propio ítem en el sidebar (`/integrations`, ver
 *    `features/integrations/`), así que aquí solo se enlaza desde el aviso de cada tarjeta.
 *  - `General`, `Métodos de pago` y `Equipo` son las tres pestañas de esta pantalla, cada una
 *    con su ruta (`/settings/general`, `/settings/payments`, `/settings/team`).
 *
 * ¿Por qué pestañas con ruta y no un estado interno? Porque el enlace se puede compartir, el
 * botón "atrás" funciona y el título del header sigue resolviéndose solo (el shell busca por
 * prefijo de ruta: `/settings/team` → "Ajustes").
 *
 * Alcance por rol: `Equipo` es solo ADMIN (el propio `GET .../users` del backend exige ese
 * rol). General y Métodos de pago se pueden LEER con cualquier rol, pero guardar es solo
 * ADMIN: las pestañas se muestran en modo consulta si el usuario no administra el negocio.
 */

const settingsTabs: TabItem[] = [
  { label: 'General', to: ROUTES.settingsGeneral, icon: SettingsIcon },
  { label: 'Métodos de pago', to: ROUTES.settingsPayments, icon: CreditCard },
  { label: 'Equipo', to: ROUTES.settingsTeam, icon: Users },
];

export function SettingsPage() {
  const { tenantId } = useActiveTenantAccess();

  if (!tenantId) {
    return (
      <EmptyState
        icon={<SlidersHorizontal size={22} />}
        title="No hay un negocio activo"
        description="Elige un negocio en el selector de la izquierda para ver y editar sus ajustes."
      />
    );
  }

  return (
    <div className="space-y-6">
      <Tabs items={settingsTabs} />
      <Outlet />
    </div>
  );
}
